import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import axios from 'axios';
import ComboCard from './ComboCard';
import ComboDetailModal from './ComboDetailModal';

const API_URL = process.env.REACT_APP_API_URL;

function CombosModal({ onClose }) {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCombo, setSelectedCombo] = useState(null);
  
  const observerTarget = useRef(null);

  useEffect(() => {
    fetchCombos(1, true);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage]);

  async function fetchCombos(page = 1, reset = false) {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/combos`, {
        params: {
          page,
          per_page: 10
        }
      });

      const newCombos = response.data.combos;
      
      if (reset) {
        setCombos(newCombos);
      } else {
        setCombos(prev => [...prev, ...newCombos]);
      }

      setHasMore(response.data.has_next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching combos:', err);
      setError('Error al cargar los combos. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }

  function loadMore() {
    if (hasMore && !loading) {
      fetchCombos(currentPage + 1, false);
    }
  }

  function handleCardClick(combo) {
    setSelectedCombo(combo);
  }

  function handleCloseDetail() {
    setSelectedCombo(null);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-primary via-brand-gold to-brand-green text-white p-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold font-montserrat">🎁 Combos Especiales</h2>
              <p className="text-sm opacity-90 font-montserrat mt-1">
                Ahorra comprando productos en paquete
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {initialLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-slate-600 font-montserrat">Cargando combos...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 font-montserrat text-center">
                {error}
              </div>
            ) : combos.length === 0 ? (
              <div className="bg-slate-50 rounded-lg p-12 text-center">
                <div className="text-6xl mb-4">🎁</div>
                <p className="text-slate-600 font-montserrat text-lg font-semibold mb-2">
                  No hay combos disponibles
                </p>
                <p className="text-slate-500 font-montserrat text-sm">
                  Los proveedores aún no han creado combos especiales
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {combos.map(combo => (
                    <ComboCard
                      key={combo.id}
                      combo={combo}
                      onClick={handleCardClick}
                    />
                  ))}
                </div>

                {/* Loading indicator for infinite scroll */}
                {loading && !initialLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                    <span className="ml-2 text-slate-600 font-montserrat">
                      Cargando más combos...
                    </span>
                  </div>
                )}

                {/* Intersection observer target */}
                <div ref={observerTarget} className="h-4" />

                {/* End message */}
                {!hasMore && combos.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-500 font-montserrat text-sm">
                      ✨ Has visto todos los combos disponibles
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Combo Detail Modal */}
      {selectedCombo && (
        <ComboDetailModal
          combo={selectedCombo}
          onClose={handleCloseDetail}
        />
      )}
    </>
  );
}

export default CombosModal;